'use client';
/**
 * LES CONVENTIONS ET ÉVÉNEMENTS D'UN CONTACT (RQ, 12/10/2026).
 *
 * « Quand je clique sur un contact je peux lui ajouter une convention, un
 * événement et je peux consulter ce qu'il y a déjà de mis en place. »
 *
 * La liste vient de `GET ged/rh/conventions?contact=CODE` ; les prochaines
 * dates, des occurrences calculées au moteur (`useOccurrences`) — l'écran ne
 * recalcule jamais une règle. Écrire suit le droit de la ligne du contact
 * (`modifiable`, calculé au serveur), que le serveur revérifie.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { couleurDe, signalerConventions, useOccurrences, type ConventionVue } from '@/lib/conventions';
import { useConfirmation } from '@/components/ui';
import { FormulaireConvention } from '@/components/FormulaireConvention';
import { Bouton } from '@/shared/Bouton';
import { aujourdhuiIso, isoJour } from '@/lib/dates';
import { fmtDate } from '@/lib/meta';

/** Les dates à venir montrées par ligne : de quoi lire le rythme, pas un agenda. */
const NB_PROCHAINES = 4;

export function ConventionsContact({ contact, modifiable }: { contact: { code: string; nom: string }; modifiable: boolean }) {
  const { showToast } = useGed();
  const { demander, confirmation } = useConfirmation();
  const [liste, setListe] = React.useState<ConventionVue[] | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [tour, setTour] = React.useState(0);
  const [edition, setEdition] = React.useState<{ valeur: ConventionVue | null; nature: 'convention' | 'evenement' } | null>(null);

  React.useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rh/conventions?contact=${encodeURIComponent(contact.code)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: { conventions?: ConventionVue[] }) => { if (vivant) { setListe(d.conventions || []); setErreur(''); } })
      .catch(() => { if (vivant) { setListe([]); setErreur('Les conventions de ce contact n’ont pas pu être lues — la liste ci-dessous n’est pas fiable.'); } });
    return () => { vivant = false; };
  }, [contact.code, tour]);

  /* Six mois devant : assez pour lire un rythme trimestriel. */
  const [{ auj, fin }] = React.useState(() => ({ auj: aujourdhuiIso(), fin: isoJour(new Date(Date.now() + 183 * 86_400_000)) }));
  const { occurrences } = useOccurrences(auj, fin);
  const prochaines = React.useMemo(() => {
    const m = new Map<string, string[]>();
    for (const o of occurrences) {
      const l = m.get(o.conventionId) || [];
      /* Une période (cycle) compte un jour par jour : on n'en garde que le début. */
      if (o.nature === 'periode' && !o.debutPeriode) continue;
      if (l.length < NB_PROCHAINES) m.set(o.conventionId, [...l, o.date]);
    }
    return m;
  }, [occurrences]);

  const relire = () => { setTour((n) => n + 1); signalerConventions(); };

  const supprimer = async (c: ConventionVue) => {
    const quoi = c.nature === 'evenement' ? 'l’événement' : 'la convention';
    if (!(await demander(`Supprimer ${quoi} « ${c.nom} » de ${contact.nom} ? Ses dates disparaissent du calendrier.`, { confirmer: 'Supprimer', danger: true }))) return;
    const r = await fetchEcriture(`/api/ged/ged/rh/conventions/${c.id}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) {
      const j = await r.json().catch(() => null);
      showToast(j?.error?.message || 'Suppression refusée.', 'erreur');
      return;
    }
    showToast(`${c.nature === 'evenement' ? 'Événement' : 'Convention'} supprimé(e).`);
    relire();
  };

  const section = (nature: 'convention' | 'evenement', titre: string, vide: string) => {
    const lignes = (liste || []).filter((c) => (c.nature || 'convention') === nature);
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</h4>
          <span style={{ flex: 1 }} />
          {modifiable && (
            <Bouton taille="sm" type="button" onClick={() => setEdition({ valeur: null, nature })}>
              {nature === 'evenement' ? '+ Ajouter un événement' : '+ Ajouter une convention'}
            </Bouton>
          )}
        </div>
        {liste === null && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture…</span>}
        {liste !== null && !lignes.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{vide}</span>}
        {lignes.map((c) => {
          const col = couleurDe(c.couleur);
          const dates = prochaines.get(c.id) || [];
          return (
            <div key={c.id} style={{ display: 'grid', gap: 3, padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', opacity: c.actif ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span aria-hidden style={{ width: 11, height: 11, borderRadius: 'var(--radius-xs)', background: col.fond, border: `2px solid ${col.texte}` }} />
                <strong style={{ fontSize: 13 }}>{c.nom}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{c.description}{c.actif ? '' : ' — désactivé(e)'}</span>
                <span style={{ flex: 1 }} />
                {modifiable && (
                  <>
                    <Bouton taille="sm" type="button" aria-label={`Modifier ${c.nom}`} onClick={() => setEdition({ valeur: c, nature: c.nature || 'convention' })}>Modifier</Bouton>
                    <Bouton taille="sm" variante="danger" type="button" aria-label={`Supprimer ${c.nom}`} onClick={() => void supprimer(c)}>Supprimer</Bouton>
                  </>
                )}
              </div>
              {c.actif && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {dates.length ? <>Prochaines dates : {dates.map((x) => fmtDate(x)).join(' · ')}</> : 'Aucune date dans les six prochains mois.'}
                  {(c.du || c.au) && <> · valable {c.du ? `du ${fmtDate(c.du)}` : ''}{c.au ? ` au ${fmtDate(c.au)}` : ''}</>}
                </span>
              )}
              {c.note && <span style={{ fontSize: 12, color: 'var(--text-body)' }}>{c.note}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 14, padding: '12px 14px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      {section('convention', 'Conventions', 'Aucune convention avec ce contact.')}
      {section('evenement', 'Événements', 'Aucun événement prévu avec ce contact.')}
      {!modifiable && (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Lecture seule : les conventions et événements de ce contact se gèrent par qui tient sa fiche (RQ, CQ, ou les achats pour un fournisseur).
        </span>
      )}
      {edition && (
        <FormulaireConvention valeur={edition.valeur} contact={contact} natureInitiale={edition.nature}
          onFerme={() => setEdition(null)} onFait={() => { setEdition(null); relire(); }} />
      )}
      {confirmation}
    </div>
  );
}
